// #45 Table, Tr, Th and Td: a real <table> (column heads in his words) inside a wrapper that scrolls sideways within its
// card. `dense` is the 7 px cell tier; `stickyHead` and `stickyFirst` keep the head row and the first column in view
// (the permission matrix); with `scroll` (a max height in px) the wrapper scrolls and is a named, focusable region.
// Rows take --t-neutral on hover and never lift; focus sits on a link inside a cell. loading: skeleton rows at the final
// geometry (aria-busy); empty: one --text-3 line across the columns. A wrapper whose table is wider than it (a narrow
// card) becomes a focusable, named region once measured, so the keyboard can scroll it (WCAG 2.1.1). At ≤ 480 each
// row becomes a card whose cells are labelled by their column (Td `label` → data-label). A region with a caption and no
// label is named by the caption. `tableClassName` styles the <table> itself; `className` the wrapper.

import { Children, isValidElement, useId, type ReactElement, type ReactNode } from "react";
import { cx } from "../lib/cx";
import { useHs, type Hs } from "../lib/hs";
import { useOverflow } from "../lib/useOverflow";
import { vars } from "../lib/vars";
import { Skeleton } from "./Skeleton";

export interface TableProps {
  /** The head row: a Tr of Th. */
  head?: ReactNode | undefined;
  /** The body rows (Tr of Td). */
  children?: ReactNode;
  /** Several <tbody> elements in place of the one body (row groups, each headed by a scope="rowgroup" cell). */
  bodies?: ReactNode | undefined;
  dense?: boolean | undefined;
  stickyHead?: boolean | undefined;
  stickyFirst?: boolean | undefined;
  /** The wrapper's max height in px: it scrolls, and becomes a focusable region named by `label`. */
  scroll?: number | undefined;
  /** The table's name (aria-label), from a labels module. */
  label?: string | undefined;
  /** A visible caption instead of a label. */
  caption?: ReactNode | undefined;
  /** Skeleton rows in place of the body (true: 3). */
  loading?: boolean | number | undefined;
  /** The body is empty: one line across the columns. */
  empty?: ReactNode | undefined;
  /** The column count (default: the head row's cells). */
  cols?: number | undefined;
  /** The wrapper's class. */
  className?: string | undefined;
  /** The <table>'s own class. */
  tableClassName?: string | undefined;
  id?: string | undefined;
}

function headCells(head: ReactNode): number {
  let count = 0;
  Children.forEach(head, (row) => {
    if (isValidElement<{ children?: ReactNode }>(row)) count = Math.max(count, Children.count(row.props.children));
  });
  return count;
}

export function Table({
  head, children, bodies, dense, stickyHead, stickyFirst, scroll, label, caption, loading, empty, cols, className, tableClassName, id,
}: TableProps) {
  const captionId = useId();
  const width = cols ?? (headCells(head) || 1);
  const skeletonRows = loading === true ? 3 : typeof loading === "number" ? loading : 0;
  let body: ReactNode = children;
  if (skeletonRows > 0) {
    body = Array.from({ length: skeletonRows }, (_, row) => (
      <tr key={row} className="mc-tr-skel">
        {Array.from({ length: width }, (__, cell) => <td key={cell}><Skeleton width={cell === 0 ? "70%" : "50%"} /></td>)}
      </tr>
    ));
  } else if (empty !== undefined && empty !== null && empty !== false) {
    body = <tr className="mc-tr-empty"><td colSpan={width}>{empty}</td></tr>;
  }
  const { ref: wrapper, overflows } = useOverflow<HTMLDivElement>();
  const region = scroll !== undefined || overflows;
  return (
    <div
      ref={wrapper}
      className={cx("mc-tbl-w", scroll !== undefined && "mc-tbl-scroll", className)}
      style={vars({ "--mc-h": scroll })}
      role={region ? "region" : undefined}
      aria-label={region ? label : undefined}
      aria-labelledby={region && label === undefined && caption !== undefined ? captionId : undefined}
      tabIndex={region ? 0 : undefined}
    >
      <table
        id={id}
        className={cx("mc-tbl", dense && "mc-tbl-dense", stickyHead && "mc-tbl-sticky-h", stickyFirst && "mc-tbl-sticky-1", tableClassName)}
        aria-label={caption === undefined ? label : undefined}
        aria-busy={skeletonRows > 0 ? true : undefined}
      >
        {caption !== undefined ? <caption className="mc-tbl-cap" id={captionId}>{caption}</caption> : null}
        {head ? <thead>{head}</thead> : null}
        {bodies !== undefined && skeletonRows === 0 && (empty === undefined || empty === null || empty === false) ? bodies : <tbody>{body}</tbody>}
      </table>
    </div>
  );
}

export interface TrProps {
  children: ReactNode;
  /** Row 108: the masked row (G5 composes it). */
  masked?: boolean | undefined;
  /** A chosen row (the --t-blue tint); the choice itself is the row's own control. */
  selected?: boolean | undefined;
  hs?: Hs | undefined;
  className?: string | undefined;
}

export function Tr({ children, masked, selected, hs, className }: TrProps) {
  const hot = useHs(hs);
  return (
    <tr className={cx(masked && "mc-masked", selected && "mc-tr-sel", className) || undefined} {...hot}>
      {children}
    </tr>
  );
}

type Align = "r" | "c";

export interface ThProps {
  children?: ReactNode;
  align?: Align | undefined;
  /** A row head (the first column of a matrix): scope="row". */
  row?: boolean | undefined;
  /** Width in px. */
  width?: number | undefined;
  className?: string | undefined;
}

export function Th({ children, align, row, width, className }: ThProps) {
  return (
    <th scope={row ? "row" : "col"} className={cx(align && `mc-${align}`, width !== undefined && "mc-th-w", className) || undefined} style={vars({ "--mc-w": width })}>
      {children}
    </th>
  );
}

export interface TdProps {
  children?: ReactNode;
  align?: Align | undefined;
  /** A second line under the value (12 px, secondary ink). */
  sub?: ReactNode | undefined;
  /** The column's words, shown before the value when the row is a card (≤ 480). */
  label?: string | undefined;
  mono?: boolean | undefined;
  nowrap?: boolean | undefined;
  colSpan?: number | undefined;
  className?: string | undefined;
}

export function Td({ children, align, sub, label, mono, nowrap, colSpan, className }: TdProps): ReactElement {
  return (
    <td className={cx(align && `mc-${align}`, mono && "mc-mono", nowrap && "mc-nw", className) || undefined} data-label={label} colSpan={colSpan}>
      {children}
      {sub !== undefined && sub !== null ? <span className="mc-sub">{sub}</span> : null}
    </td>
  );
}
